import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getConsent, OPEN_SETTINGS_EVENT, setConsent } from '@/lib/consent';

const Toggle = ({ id, label, description, checked, disabled, onChange }) => (
  <div className="flex items-start justify-between gap-4 border-b border-candy-line py-3 last:border-b-0">
    <div>
      <label htmlFor={id} className="font-bold">
        {label}
      </label>
      <p className="text-sm text-candy-ink-muted">{description}</p>
    </div>
    <span className="relative mt-0.5 inline-flex h-11 w-14 flex-none items-center justify-center">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="peer absolute inset-0 cursor-pointer appearance-none rounded-full disabled:cursor-not-allowed"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none h-7 w-12 rounded-full bg-candy-ink/30 transition-colors duration-150 peer-checked:bg-candy-ink peer-disabled:opacity-60 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-white shadow transition-transform duration-150 peer-checked:translate-x-5"
      />
    </span>
  </div>
);

// Shown once the visitor is past the age gate. "Only necessary" is the default:
// nothing optional is switched on until the visitor turns it on.
export const CookieConsent = ({ enabled }) => {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (enabled && !getConsent()) setOpen(true);
  }, [enabled]);

  useEffect(() => {
    const reopen = () => {
      const c = getConsent();
      setAnalytics(Boolean(c?.analytics));
      setMarketing(Boolean(c?.marketing));
      setCustomizing(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
  }, []);

  const save = (choice) => {
    setConsent(choice);
    setOpen(false);
    setCustomizing(false);
  };

  if (!open) return null;

  const secondary =
    'h-11 rounded-full border-2 border-candy-ink bg-candy-surface px-5 text-sm font-bold transition-colors hover:bg-candy-pink';
  const primary = 'h-11 rounded-full bg-candy-ink px-5 text-sm font-bold text-white transition-colors hover:bg-candy-ink/90';

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${id}-title`}
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 z-[45] max-h-[85vh] overflow-y-auto rounded-card bg-candy-surface p-5 shadow-[0_20px_50px_-20px_rgb(var(--ink)/0.45)] animate-in slide-in-from-bottom-4 fade-in-0 duration-200 md:inset-x-auto md:bottom-6 md:left-6 md:w-[420px] md:p-6"
    >
      <h2 id={`${id}-title`} className="font-heading text-xl font-extrabold tracking-[-0.02em]">
        {t('cookies.title')}
      </h2>
      <p className="mt-1.5 text-sm text-candy-ink-muted">{t('cookies.text')}</p>

      {customizing && (
        <div className="mt-3">
          <Toggle
            id={`${id}-necessary`}
            label={t('cookies.necessary')}
            description={t('cookies.necessaryDesc')}
            checked
            disabled
          />
          <Toggle
            id={`${id}-analytics`}
            label={t('cookies.analytics')}
            description={t('cookies.analyticsDesc')}
            checked={analytics}
            onChange={setAnalytics}
          />
          <Toggle
            id={`${id}-marketing`}
            label={t('cookies.marketing')}
            description={t('cookies.marketingDesc')}
            checked={marketing}
            onChange={setMarketing}
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {customizing ? (
          <>
            <button type="button" className={`${primary} flex-1`} onClick={() => save({ analytics, marketing })} data-testid="cookie-save">
              {t('cookies.save')}
            </button>
            <button type="button" className={`${secondary} flex-1`} onClick={() => save({ analytics: true, marketing: true })}>
              {t('cookies.acceptAll')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className={`${primary} flex-1`} onClick={() => save({ analytics: false, marketing: false })} data-testid="cookie-necessary">
              {t('cookies.onlyNecessary')}
            </button>
            <button type="button" className={`${secondary} flex-1`} onClick={() => save({ analytics: true, marketing: true })} data-testid="cookie-accept-all">
              {t('cookies.acceptAll')}
            </button>
            <button
              type="button"
              className="h-11 basis-full rounded-full text-sm font-bold underline underline-offset-2"
              onClick={() => setCustomizing(true)}
              data-testid="cookie-customize"
            >
              {t('cookies.customize')}
            </button>
          </>
        )}
      </div>
    </section>
  );
};
